import { ArrowRight } from "lucide-react";
import { ModifiedRow } from "../lib/diff";
import { displayKey } from "../types/state";

interface AddRemoveProps {
  kind: "added" | "removed";
  row: Record<string, string>;
  keyColumns: string[];
  onGoToRow?: () => void;
}

export function AddRemoveCard({ kind, row, keyColumns, onGoToRow }: AddRemoveProps) {
  const label = displayKey(row, keyColumns) || "(sans clé)";
  const keySet = new Set(keyColumns);
  const other = Object.entries(row)
    .filter(([k, v]) => !keySet.has(k) && v)
    .slice(0, 3);
  const color =
    kind === "added"
      ? "border-emerald-200 bg-emerald-50"
      : "border-rose-200 bg-rose-50";
  const sign = kind === "added" ? "+" : "−";
  const signColor = kind === "added" ? "text-emerald-700" : "text-rose-700";

  return (
    <div className={`border rounded-md px-3 py-2 text-sm ${color}`}>
      <div className="flex items-start gap-2">
        <span className={`font-medium ${signColor}`}>{sign}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-stone-800 truncate">{label}</div>
          {other.length > 0 && (
            <div className="text-xs text-stone-600 mt-0.5">
              {other.map(([k, v]) => (
                <div key={k} className="truncate">
                  <span className="text-stone-500">{k} :</span> {v}
                </div>
              ))}
            </div>
          )}
          {onGoToRow && kind !== "removed" && (
            <button
              className="text-xs text-stone-600 underline mt-1 hover:text-stone-900"
              onClick={onGoToRow}
            >
              Aller à la ligne
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ModifiedProps {
  modified: ModifiedRow;
  keyColumns: string[];
  onGoToRow?: () => void;
}

export function ModifiedCard({ modified, keyColumns, onGoToRow }: ModifiedProps) {
  const label = displayKey(modified.row, keyColumns) || modified.key;

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-md px-3 py-2 text-sm">
      <div className="flex items-start gap-2">
        <span className="font-medium text-amber-700">~</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-stone-800 truncate">{label}</div>
          <div className="text-xs text-stone-700 mt-1 space-y-1">
            {Object.entries(modified.changes).map(([col, { before, after }]) => (
              <div key={col} className="break-words">
                <div className="text-stone-500">{col} :</div>
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="line-through text-stone-500">{before || "∅"}</span>
                  <ArrowRight className="w-3 h-3 text-stone-500" />
                  <span className="text-stone-900">{after || "∅"}</span>
                </div>
              </div>
            ))}
          </div>
          {onGoToRow && (
            <button
              className="text-xs text-stone-600 underline mt-1 hover:text-stone-900"
              onClick={onGoToRow}
            >
              Aller à la ligne
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
